import ProductService from "../services/ProductService";
import ProductOptionService from "../services/ProductOptionService";
import PricingService from "../services/PricingService";
import cloudinary from "cloudinary-core";
import DOMService from "../services/DOMService";
import CompatibleProductsService from "../services/CompatibleProductsService";

export default {
  namespaced: true,
  state: {
    product: null,
    seriesId: "",
    selectedOptions: [],
    isFirstRun: true,
    isLoading: true,
    skus: [],
    isProductFirstLoad: true,
    showCompatibleProducts: true,
    isLeadTimeLoading: false,
    breadCrumbs: null,
    isBreadCrumbsLoading: false,
    hasProductLoaded: false,
    isPricingInProgress: false,
    hasPricingLoaded: false,
    basicSAPLeadTime: null,
    hasBasicSAPLeadTimeTriggered: false
  },
  getters: {
    // ENHANCED PDP
    getEnhancedProductData: state => state.enhancedProductData,
    showEnhancedOption: state =>
      state.enhancedProductData
        ? state.enhancedProductData.DisplayProductOptions
        : false,
    getEnhancedPDPOptions: state => {
      if (!state.enhancedProductData) {
        return state.enhancedProductData;
      }
      return state.enhancedProductData.enhancedPdpOptions;
    },
    getTestTogSectionEditMode: state => state.testTogSectionEditMode,
    // this gets all the state we need for enhanced products
    // we are using this getter to set local storage
    getEnhancedSuperState: (state, getters, rootState) => ({
      seriesId: rootState.product.seriesId,
      enhancedproduct: state,
      enhancedBom: rootState.enhancedBom
    }),

    // ORIGINAL PDP
    getDisplayProduct: state => state.product && state.product.DisplayProduct,
    getDisplayProductOptions: state =>
      state.product && state.product.DisplayProductOptions,
    // getDisplayInventoryQuantityCount: state =>
    //   state.product.DisplayInventoryQuantityCount,
    // getInventoryQuantityCount: state =>
    //   state.product.SelectedSku.InventoryQuantityAvailable,
    getProduct: state => state.product,
    getSeriesId: state => state.seriesId,
    getProductSeries: state => state.product && state.product.Series,
    getProductSku: state => state.product && state.product.SelectedSku,
    basicSAPLeadTime: state => state.basicSAPLeadTime,
    isSkuPage: (state, getters) => getters.getProductSku !== null,
    getSelectedSkuCode: state => state.product?.SelectedSku?.SkuCode,
    isSeriesPage: (state, getters) => !getters.isSkuPage,
    getProductBanner: state => {
      if (state.product?.SelectedSku?.Banner?.[0]) {
        return state.product?.SelectedSku?.Banner?.[0];
      } else if (
        !state.product?.SelectedSku &&
        state.product?.Series?.Banner?.[0]
      ) {
        return state.product?.Series?.Banner?.[0];
      } else {
        return null;
      }
    },
    // TODO: Either write a polyfill in utility or import an external one
    // possible polyfill https://github.com/ungap/url-search-params
    getProductTitle(state, getters) {
      const { getProduct } = getters;
      if (
        getProduct &&
        getProduct.SelectedSku &&
        getProduct.SelectedSku.Title
      ) {
        let title = getProduct.SelectedSku.Title;
        let uri = window.location.search.substring(1);
        if (uri) {
          let params = new URLSearchParams(uri);
          if (params) {
            let manufacturer = params.get("make");
            let model = params.get("model");
            if (manufacturer && model) {
              title += " - for " + manufacturer + " " + model;
            }
          }
        }
        return title;
      } else if (getProduct && getProduct.Series && getProduct.Series.Title)
        return getProduct.Series.Title;
      else return "";
    },
    getProductCompatibility: (state, getters) => {
      const { getProduct } = getters;

      if (getProduct) {
        return getProduct.DisplayCompatibleProductsSection;
      }
    },
    getProductDescription(state, getters) {
      const { getProduct } = getters;
      if (
        getProduct &&
        getProduct.SelectedSku &&
        getProduct.SelectedSku.Description
      )
        return getProduct.SelectedSku.Description;
      else if (getProduct && getProduct.Series && getProduct.Series.Description)
        return getProduct.Series.Description;
      else return "";
    },

    getProductConfigureMessage(state, getters) {
      const { getProduct } = getters;
      if (
        getProduct &&
        getProduct.SelectedSku &&
        getProduct.SelectedSku.ConfigureMessage
      )
        return getProduct.SelectedSku.ConfigureMessage;
      else return "";
    },
    getIsConfigurable: state =>
      state.product &&
      state.product.SelectedSku &&
      state.product.SelectedSku.IsConfigurable,
    getConfigureButtonText: state =>
      state.product &&
      state.product.SelectedSku &&
      state.product.SelectedSku.ConfigureButtonText,
    getToolUrl: state =>
      state.product &&
      state.product.SelectedSku &&
      state.product.SelectedSku.ToolUrl,

    getResources(state, getters) {
      const { getProduct } = getters;
      if (getProduct && getProduct.Resources) return getProduct.Resources;
      else return "";
    },
    getImage: (state, getters) => {
      var product = getters.getProduct;
      if (product == null) {
        return { Url: "", Title: "" };
      }

      /**
        this image handles the case where no image is provided
       **/
      var image = [
        {
          Url:
            "https://res.cloudinary.com/avd/image/upload/v1576013897/ProductPage/General/No-Image.jpg",
          Title: "No Photo Available"
        }
      ];
      if (
        product &&
        product.SelectedSku &&
        product.SelectedSku.Images &&
        product.SelectedSku.Images.LargeSize.length > 0
      ) {
        image = product.SelectedSku.Images.LargeSize;
      } else if (
        product &&
        product.Series &&
        product.Series.Images &&
        product.Series.Images.LargeSize.length > 0
      ) {
        image = product.Series.Images.LargeSize;
      }

      return image;
    },
    getSelectedOptions: state => state.selectedOptions,
    getOptionDefinitions: state => {
      if (state.product) {
        return state.product.OptionDefinitions;
      }
      return null;
    },
    getSkus: state => state.product && state.product.Skus,
    getSkuOptions: state => {
      return state.product.Skus.filter(sku => sku.OptionValues != null)
        .map(sku => sku.OptionValues)
        .reduce((acc, currentValue) => [...acc, ...currentValue], []);
    },
    getProductPrice: (state, getters) =>
      getters.getProductSku !== null ? getters.getProductSku.Price : null,
    hasPrice: (state, getters) =>
      getters.getProductPrice && getters.getProductPrice !== null
        ? getters.getProductPrice.RawPrice > 0
        : false,
    hasMSRP: (state, getters) =>
      getters.getProductPrice !== null
        ? getters.getProductPrice.RawMSRP > 0
        : false,
    getProductFormattedPrice: (state, getters) =>
      getters.getProductSku && getters.getProductSku.Price
        ? getters.getProductSku.Price.FormattedPrice
        : "",
    getProductFormattedMSRP: (state, getters) =>
      getters.getProductSku && getters.getProductSku.Price
        ? getters.getProductSku.Price.FormattedMSRP
        : null,
    getCurrencyCode: (state, getters) =>
      getters.getProductSku && getters.getProductSku.Price
        ? getters.getProductSku.Price.CurrencyCode
        : "",
    getCurrencySymbol: (state, getters) =>
      getters.getProductSku && getters.getProductSku.Price
        ? getters.getProductSku.Price.Currency
        : "",
    getIsAddToCartHidden(state, getters) {
      const { getProductSku } = getters;
      if (
        getProductSku &&
        getProductSku.SAPLeadTime &&
        getProductSku.SAPLeadTime.length > 0
      ) {
        return getProductSku.SAPLeadTime[0].IsAddToCartHidden == true;
      }
    },
    getIsAddToQuoteHidden(state, getters) {
      const { getProductSku } = getters;
      if (
        getProductSku &&
        getProductSku.SAPLeadTime &&
        getProductSku.SAPLeadTime.length > 0
      ) {
        return getProductSku.SAPLeadTime[0].IsAddToQuoteHidden == true;
      }
    },
    getPriceReplaceMessage(state, getters) {
      const { getProductSku } = getters;
      if (
        getProductSku &&
        getProductSku.SAPLeadTime &&
        getProductSku.SAPLeadTime.length > 0
      ) {
        return getProductSku.SAPLeadTime[0].PriceReplacementMessage;
      }
    },
    isFirstRun: state => state.isFirstRun,
    isLoading: state => state.isLoading,
    hasProductLoaded: state => state.hasProductLoaded,
    getZoomerImages: (state, getters) => {
      if (
        getters.getProduct &&
        getters.getProduct.SelectedSku &&
        getters.getProduct.SelectedSku.Images != null &&
        getters.getProduct.SelectedSku.Images.Thumbs.length > 0
      ) {
        let allImages = getters.getProduct.SelectedSku.Images;

        if (
          getters.getProduct.Series &&
          getters.getProduct.Series.Videos != null &&
          getters.getProduct.Series.Videos.Thumbs.length > 0
        ) {
          // Large Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.LargeSize.length;
            i++
          ) {
            if (
              allImages.LargeSize.indexOf(
                getters.getProduct.Series.Videos.LargeSize[i]
              ) === -1
            ) {
              allImages.LargeSize.push(
                getters.getProduct.Series.Videos.LargeSize[i]
              );
            }
          }
          // Thumbs
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.Thumbs.length;
            i++
          ) {
            if (
              allImages.Thumbs.indexOf(
                getters.getProduct.Series.Videos.Thumbs[i]
              ) === -1
            ) {
              allImages.Thumbs.push(getters.getProduct.Series.Videos.Thumbs[i]);
            }
          }

          // Normal Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.NormalSize.length;
            i++
          ) {
            if (
              allImages.NormalSize.indexOf(
                getters.getProduct.Series.Videos.NormalSize[i]
              ) === -1
            ) {
              allImages.NormalSize.push(
                getters.getProduct.Series.Videos.NormalSize[i]
              );
            }
          }

          // Medium Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.MediumSize.length;
            i++
          ) {
            if (
              allImages.MediumSize.indexOf(
                getters.getProduct.Series.Videos.MediumSize[i]
              ) === -1
            ) {
              allImages.MediumSize.push(
                getters.getProduct.Series.Videos.MediumSize[i]
              );
            }
          }
        }

        for (let i = 0; i < allImages.Thumbs.length; i++) {
          allImages.Thumbs[i].id = i;
        }
        for (let i = 0; i < allImages.NormalSize.length; i++) {
          allImages.NormalSize[i].id = i;
        }
        for (let i = 0; i < allImages.LargeSize.length; i++) {
          allImages.LargeSize[i].id = i;
        }
        for (let i = 0; i < allImages.MediumSize.length; i++) {
          allImages.MediumSize[i].id = i;
        }

        return allImages;
      } else if (
        getters.getProduct &&
        getters.getProduct.Series &&
        getters.getProduct.Series.Images != null &&
        getters.getProduct.Series.Images.Thumbs.length > 0
      ) {
        let allImages = getters.getProduct.Series.Images;

        if (
          getters.getProduct.Series &&
          getters.getProduct.Series.Videos != null &&
          getters.getProduct.Series.Videos.Thumbs.length > 0
        ) {
          // Large Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.LargeSize.length;
            i++
          ) {
            allImages.LargeSize.push(
              getters.getProduct.Series.Videos.LargeSize[i]
            );
          }
          // Thumbs
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.Thumbs.length;
            i++
          ) {
            allImages.Thumbs.push(getters.getProduct.Series.Videos.Thumbs[i]);
          }

          // Normal Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.NormalSize.length;
            i++
          ) {
            allImages.NormalSize.push(
              getters.getProduct.Series.Videos.NormalSize[i]
            );
          }

          // Medium Size
          for (
            let i = 0;
            i < getters.getProduct.Series.Videos.MediumSize.length;
            i++
          ) {
            allImages.MediumSize.push(
              getters.getProduct.Series.Videos.MediumSize[i]
            );
          }
        }

        for (let i = 0; i < allImages.Thumbs.length; i++) {
          allImages.Thumbs[i].id = i;
        }
        for (let i = 0; i < allImages.NormalSize.length; i++) {
          allImages.NormalSize[i].id = i;
        }
        for (let i = 0; i < allImages.LargeSize.length; i++) {
          allImages.LargeSize[i].id = i;
        }
        for (let i = 0; i < allImages.MediumSize.length; i++) {
          allImages.MediumSize[i].id = i;
        }

        return allImages;
      }

      /**
        If we reach this point there is no main image and we need to provide a fallback
       **/
      const defaultImage = {
        Url:
          "https://res.cloudinary.com/avd/image/upload/v1576013897/ProductPage/General/No-Image.jpg",
        Title: "No Photo Available"
      };

      return {
        Thumbs: [defaultImage],
        NormalSize: [defaultImage],
        LargeSize: [defaultImage]
      };
    },
    getUniqueProductOptions: state => {
      return ProductOptionService.buildProductOptions1(
        state.product.OptionDefinitions,
        state.product.Skus,
        state.selectedOptions
      );
    },
    getIsProductFirstLoad: state => state.isProductFirstLoad,
    getShowCompatibleProducts: state => {
      if (state.product) {
        const seriesCompatible = state.product.Series.HasCompatibleProducts;
        const skuCompatible = state.product.Series.AnySkuHaveCompatibleProducts;
        const showCompatible = seriesCompatible || skuCompatible;
        return showCompatible;
      }
      return state.showCompatibleProducts;
    },
    getProductBrandName: state => {
      if (
        state.product &&
        state.product.Series &&
        state.product.Series.BrandDetails
      ) {
        const brandName = state.product.Series.BrandDetails[0].Name;
        return brandName;
      }
    },

    getProductBrandDetails: state =>
      state.product &&
      state.product.Series &&
      state.product.Series.BrandDetails,
    getProductInformationText: state =>
      state.product && state.product.ProductInformationText,
    getProductNotAvailableText: state =>
      state.product && state.product.ProductNotAvailableText,

    getProductStatus: state =>
      state.product &&
      state.product.SelectedSku &&
      state.product.SelectedSku.ProductStatus,

    getIsLeadTimeLoading: state => state.isLeadTimeLoading,
    getBreadCrumbs: state => state.breadCrumbs,
    getIsBreadCrumbsLoading: state => state.isBreadCrumbsLoading,
    isPricingInProgress: state => state.isPricingInProgress,
    hasPricingLoaded: state => state.hasPricingLoaded,
    hasBasicSAPLeadTimeTriggered: state => state.hasBasicSAPLeadTimeTriggered
  },
  mutations: {
    // ORIGINAL PDP
    UPDATE_SERIES_ID(state, seriesId) {
      state.seriesId = seriesId;
    },
    UPDATE_PRODUCT(state, newProduct) {
      state.product = newProduct;
      if (newProduct.Skus) {
        state.skus = newProduct.Skus;
      }
      // If product price exists but SelectedSku.Price is missing, assign it
      if (
        state.product?.SelectedSku &&
        state.productprice &&
        !state.product.SelectedSku.Price
      ) {
        this._vm.$set(state.product.SelectedSku, "Price", state.productprice);
      }
    },
    UPDATE_IS_FIRST_RUN(state, value) {
      state.isFirstRun = value;
    },
    UPDATE_IS_LOADING(state, value) {
      state.isLoading = value;
    },
    SET_HAS_PRODUCT_LOADED(state, value) {
      state.hasProductLoaded = value;
    },
    SET_HAS_BASIC_SAPLEADTIME_TRIGGERED(state, value) {
      state.hasBasicSAPLeadTimeTriggered = value;
    },
    UPDATE_SELECTED_OPTION(state, selection) {
      if (state.product && state.product.OptionDefinitions.length) {
        var includedAready = false;
        var isValidSelection = false;
        var indexOfCurrentOption;
        state.selectedOptions.forEach((element, index) => {
          if (selection.Key == element.Key) {
            includedAready = true;
            indexOfCurrentOption = index;
            element.Value = selection.Value;
          }
        });

        if (
          indexOfCurrentOption !== undefined &&
          includedAready &&
          selection.showOption === "default" &&
          !selection.fromService
        ) {
          if (state.selectedOptions.indexOf(indexOfCurrentOption) == -1) {
            // state.selectedOptions.splice(indexOfCurrentOption, 1);
            state.selectedOptions = state.selectedOptions.filter(
              (_, index) => index !== indexOfCurrentOption
            );
          }
        }
        state.product.OptionDefinitions.forEach(element => {
          if (selection.Key == element.Key) {
            isValidSelection = true;
          }
        });

        if (
          !includedAready &&
          isValidSelection &&
          selection.showOption !== "default"
        ) {
          //state.selectedOptions.push(selection);
          state.selectedOptions = [...state.selectedOptions, selection];
        }
      }
    },
    UPDATE_SELECTED_SKU_PRICE(state, skuPrice) {
      const formattedPrice = Array.isArray(skuPrice) ? skuPrice : [skuPrice];
      if (!formattedPrice.length) return;

      this._vm.$set(state, "productprice", formattedPrice[0]); // Store price in separate state variable
      if (state.product?.SelectedSku) {
        this._vm.$set(state.product.SelectedSku, "Price", formattedPrice[0]);
      } else {
        // Watch the product state SelectedSku to update the price when available
        const unwatch = this._vm.$watch(
          () => state.product?.SelectedSku,
          newVal => {
            if (newVal) {
              this._vm.$set(newVal, "Price", formattedPrice[0]);
              unwatch(); // Stop watching after setting the value
            }
          }
        );
      }
    },
    SET_SAP_LEAD_TIME(state, leadTime) {
      state.basicSAPLeadTime = leadTime;
    },
    SET_HAS_PRICING_API_STARTED(state, value) {
      // Update the hasPricingLoaded state
      state.isPricingInProgress = value;
    },
    SET_HAS_PRICING_LOADED(state, value) {
      // Update the hasPricingLoaded state
      state.hasPricingLoaded = value;
    },
    CLEAR_SELECTED_OPTION(state) {
      state.selectedOptions = [];
    },
    MARK_IS_PRODUCT_FIRST_LOAD(state) {
      state.isProductFirstLoad = false;
    },
    UPDATE_LEAD_TIME(state, leadTime) {
      const updatedLeadTime = leadTime.SAPDelivaryDate;
      state.product.SelectedSku.SAPLeadTime[0].SAPDelivaryDate = `${updatedLeadTime} 12:00:00 AM`;
      state.product.SelectedSku.SAPLeadTime[0].SAPStatusCode =
        leadTime.SAPStatusCode;
      state.product.SelectedSku.SAPLeadTime[0].Icon = leadTime.Icon;
      state.product.SelectedSku.SAPLeadTime[0].Message = leadTime.Message;
    },
    UPDATE_LEAD_TIME_WITH_STATE(state, leadTime) {
      const updatedLeadTime = leadTime.SAPDelivaryDate;
      state.product.SelectedSku.SAPLeadTime[0].SAPDelivaryDate = `${updatedLeadTime} 12:00:00 AM`;
      state.product.SelectedSku.SAPLeadTime[0].SAPStatusCode =
        leadTime.SAPStatusCode;
      state.product.SelectedSku.SAPLeadTime[0].Icon = leadTime.Icon;
      state.product.SelectedSku.SAPLeadTime[0].Message = leadTime.Message;
    },
    UPDATE_IS_LEADTIME_LOADING(state, isLeadTimeLoading) {
      state.isLeadTimeLoading = isLeadTimeLoading;
    },
    UPDATE_BREAD_CRUMBS(state, breadCrumbs) {
      state.breadCrumbs = breadCrumbs;
    },
    UPDATE_IS_BREAD_CRUMBS_LOADING(state, isBreadCrumbsLoading) {
      state.isBreadCrumbsLoading = isBreadCrumbsLoading;
    }
  },

  actions: {
    setSeriesId({ commit }, { seriesId }) {
      commit("UPDATE_SERIES_ID", seriesId);
    },
    loadProduct(
      { commit, getters },
      { seriesId, skuId, useOptimProduct = false }
    ) {
      commit("UPDATE_IS_LOADING", true);
      commit("SET_HAS_PRODUCT_LOADED", false);
      commit("SET_HAS_PRICING_LOADED", false);

      const productApiFunction = useOptimProduct
        ? ProductService.getProductOptimized
        : ProductService.getProduct;

      return productApiFunction(seriesId, skuId)
        .then(response => {
          return response.data;
        })
        .then(data => {
          handleProductResponseData(
            data,
            commit,
            getters.getIsProductFirstLoad
          );
          return data;
        })
        .then(() => commit("UPDATE_IS_LOADING", false))
        .then(() => {
          //
          var MvcSkeleton = document.getElementById("MvcSkeleton");
          MvcSkeleton.setAttribute("style", "display: none");
        })
        .then(() => {
          const cloudinaryCore = new cloudinary.Cloudinary({
            cloud_name: "demo"
          });
          cloudinaryCore.responsive();
        })
        .catch(e => {
          // TODO: error handling
          throw e;
        })
        .finally(() => {
          commit("SET_HAS_PRODUCT_LOADED", true);
        });
    },
    loadProductNoSkeleton(
      { commit, getters },
      { seriesId, skuId, useOptimProduct = false }
    ) {
      const productApiFunction = useOptimProduct
        ? ProductService.getProductOptimized
        : ProductService.getProduct;

      return productApiFunction(seriesId, skuId)
        .then(response => {
          return response.data;
        })
        .then(data => {
          handleProductResponseData(data, commit, getters.isProductFirstLoad);
          return data;
        })
        .then(() => {
          const cloudinaryCore = new cloudinary.Cloudinary({
            cloud_name: "demo"
          });
          cloudinaryCore.responsive();
        })
        .catch(e => {
          // TODO: error handling
          throw e;
        });
    },
    loadProductPrice(
      { commit, getters, rootGetters },
      { store, skuCode = null } = {}
    ) {
      if (
        rootGetters["user/getIsUserLoggedIn"] &&
        (skuCode || getters.getSelectedSkuCode)
      ) {
        commit("SET_HAS_PRICING_API_STARTED", true);
        commit("SET_HAS_PRICING_LOADED", false);
        return PricingService.getPriceBySku(
          skuCode || getters.getSelectedSkuCode,
          store
        )
          .then(({ data }) => data)
          .then(data => {
            commit("UPDATE_SELECTED_SKU_PRICE", data);
          })
          .catch(e => {
            // TODO: error handling
            throw e;
          })
          .finally(() => {
            commit("SET_HAS_PRICING_API_STARTED", false);
            commit("SET_HAS_PRICING_LOADED", true);
          });
      }
    },
    loadBreadCrumbs({ commit }, { skuId, seriesId }) {
      return ProductService.fetchBreadCrumbs(skuId, seriesId)
        .then(response => {
          return response.data;
        })
        .then(data => commit("UPDATE_BREAD_CRUMBS", data))
        .then(() => commit("UPDATE_IS_BREAD_CRUMBS_LOADING", false))
        .catch(e => {
          // TODO: error handling
          throw e;
        });
    },
    loadBasicSAPLeadTime({ commit }, skuCode) {
      commit("SET_HAS_BASIC_SAPLEADTIME_TRIGGERED", true);
      ProductService.getBasicSAPLeadTime(skuCode)
        .then(response => {
          commit("SET_HAS_BASIC_SAPLEADTIME_TRIGGERED", false);
          return JSON.parse(response.data);
        })
        .then(response => commit("SET_SAP_LEAD_TIME", response));
    },
    updateIsBreadCrumbsLoading({ commit }, value) {
      commit("UPDATE_IS_BREAD_CRUMBS_LOADING", value);
    },
    selectOption({ commit }, selection) {
      commit("UPDATE_SELECTED_OPTION", selection);
    },
    clearSelectedOption({ commit }, product) {
      commit("CLEAR_SELECTED_OPTION");
      CompatibleProductsService.fireProductClearAllEvent(product);
    },
    updateIsFirstRun({ commit }, value) {
      commit("UPDATE_IS_FIRST_RUN", value);
    },
    markIsProductFirstLoad({ commit }) {
      commit("MARK_IS_PRODUCT_FIRST_LOAD");
    },
    setIsLeadTimeLoading({ commit }, value) {
      commit("UPDATE_IS_LEADTIME_LOADING", value);
    },
    updateLeadTime({ commit, getters, rootGetters }, store) {
      if (
        rootGetters["user/getIsUserLoggedIn"] &&
        getters.getSelectedSkuCode &&
        !rootGetters["addToCartCount/getShowError"]
      ) {
        return PricingService.getPriceBySku(getters.getSelectedSkuCode, store)
          .then(({ data }) => data)
          .then(data => commit("UPDATE_LEAD_TIME", data[0].LeadTimeDefinition))
          .then(() => {
            commit("UPDATE_IS_LEADTIME_LOADING", false);
          })
          .catch(e => {
            // TODO: error handling
            throw e;
          });
      }
    }
  }
};

/* Product Store helpers */

// gets the correct text for compatibale products title
const getProductCodeOrTitle = data => {
  if (!data.SelectedSku) {
    return data.Series.Title;
  } else {
    return data.SelectedSku.Title;
  }
};

// handles then callback for response data
const handleProductResponseData = (data, commit, isProductFirstLoad) => {
  commit("UPDATE_PRODUCT", data);
  if (true === isProductFirstLoad) {
    CompatibleProductsService.fireProductLoadedEvent(data);
    //console.log("Product Loaded event " + JSON.stringify(data));
    commit("MARK_IS_PRODUCT_FIRST_LOAD");
  } else {
    CompatibleProductsService.fireProductUpdatedEvent(data);
  }
  updateCompatibleProductsDOM(data);
};

// handles all the DOM updates when COVEO compatible products section needs to be updated
function updateCompatibleProductsDOM(data) {
  const productText = getProductCodeOrTitle(data);
  const displayShowSelectSkuMsg =
    data.SelectedSku == null &&
    data.Series &&
    !data.Series.HasCompatibleProducts &&
    data.Series.AnySkuHaveCompatibleProducts
      ? true
      : false;

  if (productText) DOMService.ReplaceSearchResultsText(productText);

  CompatibleProductsService.handleHideShowCompatibleProducts(data);
  DOMService.showHideSelectSkuMsg(displayShowSelectSkuMsg);
}
